import React, { useState, useContext, Fragment } from 'react';
import { Table, UncontrolledTooltip } from 'reactstrap';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { Esap, FlowType, FlowVersion } from '../../../../services/types/Flow';
import {
  Button,
  Modal,
  LinkButton,
  FileUploadButton,
  ConfirmModal,
} from '../../../../shared/components';
import {
  dateTimeFormat,
  getFlowLinkName,
  tooltipPreventOverflowModifiers,
} from '../../../../shared/misc/utils';
import FlowStatusBadge from './FlowStatusBadge';
import PerformanceBadge from '../../PerformanceBadge';
import EsrrBadge from '../../EsrrBadge';
import SprTable from './Spr/SprTable';
import { OperationBasicItem } from '../../../../services/types/Operation';
import OperationContext from '../context';
import FlowRelatedSelection from './FlowRelatedSelection';
import CurrentEsap from './Esrs/CurrentEsap';
import Role from '../../../../types/role';
import Context from '../../../App/MainProvider/context';
import FlowDeleteModal from './FlowDeleteModal';

interface Props {
  data: FlowVersion;
  openWord?: () => void;
  handleDownloadDoc: (versionId: number) => void;
  handleUpload: (fData: FormData) => void;
  language?: string;
  flowType: FlowType;
  flowTypeName: string;
  canEdit: boolean;
  relatedOperations: OperationBasicItem[];
  canRelate: boolean;
  setRelatedOperationIds: (values: number[]) => void;
  handleRelate: () => void;
  operationOwner: OperationBasicItem;
  setShowReviewersModal: (show: boolean) => void;
  showBtnReviewers: boolean;
  createEsap: boolean | null;
  lastEsap: Esap | null;
  hasEsap: boolean;
  changesEsapContent: boolean | null;
  hasEsapContent: boolean | null;
  currentEsapHasContent: boolean | null;
  currentRelatedOperationIds: number[];
  deleteAction: (deleteType: string) => void;
}

const FlowCurrentVersionsTable: React.FC<Props> = (props) => {
  const {
    data,
    openWord,
    handleDownloadDoc,
    handleUpload,
    language,
    flowType,
    flowTypeName,
    canEdit,
    relatedOperations,
    canRelate,
    setRelatedOperationIds,
    handleRelate,
    operationOwner,
    setShowReviewersModal,
    showBtnReviewers,
    createEsap,
    lastEsap,
    hasEsap,
    changesEsapContent,
    hasEsapContent,
    currentEsapHasContent,
    currentRelatedOperationIds,
    deleteAction,
  } = props;
  const {
    operation: { id: opId },
    reload,
  } = useContext(OperationContext);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [showRationaleModal, setShowRationaleModal] = useState(false);
  const [showRelateModal, setShowRelateModal] = useState(false);
  const [errorUpload, setErrorUpload] = useState<string | null>(null);
  const isLegacy = data.source === 'Legacy_Convergence';

  const uploadFileError = (textError: string) => {
    let error = textError;
    if (error.toLocaleLowerCase() === 'oversize') {
      error = t('uploadMaxSizeError30');
    }
    setErrorUpload(error);
  };

  const { user, inSuperUserMode } = useContext(Context);

  const showRationale = () => {
    setShowRationaleModal(true);
  };

  const maxRelated = () =>
    (flowType === 'INITIAL_ESRS' || flowType === 'ESRS' || flowType === 'SR_ESPF') &&
    currentRelatedOperationIds.length > 4;

  data.comments.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

  const isChildren = () => opId !== operationOwner.id;

  const downloadEzshare = (ezShareCode: string) => {
    window.open(`https://idbdocs.iadb.org/wsdocs/getdocument.aspx?docnum=${ezShareCode}`, '_blank');
  };
  const renderRelateLink = () => {
    if (relatedOperations.length > 0) {
      const relateds = [...relatedOperations, operationOwner].filter((x) => x.id !== opId);
      const opRelText = relateds.map((x) => x.number).join(', ');
      return canRelate ? (
        <Button
          className="btn-tertiary no-padding"
          text={opRelText}
          onClick={() => setShowRelateModal(true)}
        />
      ) : (
        <>
          {relateds.map((r, i) => (
            <Fragment key={`relatedLink_${r.number}`}>
              {i !== 0 && ','}
              <Button
                className="btn-tertiary p-0 mr-0"
                text={r.number}
                onClick={() => {
                  reload(r.number.toString());
                  navigate(`/operations/${r.number}/${getFlowLinkName(flowType)}`, {
                    replace: true,
                  });
                }}
              />
            </Fragment>
          ))}
        </>
      );
    }
    return (
      canRelate && (
        <Button
          className="btn-tertiary no-padding"
          text="Add Operation"
          onClick={() => setShowRelateModal(true)}
        />
      )
    );
  };

  const createEsapValue = () => {
    if (data.status === 'DRAFT' || createEsap === null) {
      return '';
    }
    if (hasEsap && hasEsapContent && !changesEsapContent) {
      return t('sameVersion');
    }
    return (createEsap && hasEsapContent) || changesEsapContent ? t('newVersion') : t('noEsap');
  };

  const isSR = flowType === 'SR' || flowType === 'SR_ESPF';

  const canDelete =
    (flowType === 'INITIAL_ESRS' || flowType === 'ESRS' || isSR) &&
    (user.roleCode === Role.Admin || inSuperUserMode());

  const [showDeleteFlowModal, setShowDeleteFlowModal] = useState(false);

  const versionTr = (x: FlowVersion) => {
    const versionText = isLegacy
      ? `${flowTypeName} v.${'0'}`
      : `${flowTypeName} v.${x.lap.toString()}.${x.version.toString()}`;
    return (
      <>
        <tr>
          <td>
            {!isLegacy ? (
              <LinkButton onClick={openWord} icon="file-word" text={versionText} />
            ) : (
              <LinkButton
                onClick={() => downloadEzshare(x.ezShareDocumentCode)}
                icon="file-word"
                text={versionText}
                isLegacy={isLegacy}
              />
            )}
          </td>
          <td>
            <FlowStatusBadge code={x.status} />
          </td>
          {isSR && (
            <>
              <td>{x.spr && <PerformanceBadge code={x.spr.rating.substring(1)} />}</td>
              <td>{x.spr && x.spr.esrrRating && <EsrrBadge text={x.spr.esrrRating} />}</td>
              <td>
                {x.spr && (
                  <Button
                    className="btn-tertiary no-padding"
                    icon="clipboard-list"
                    text=""
                    onClick={() => showRationale()}
                  />
                )}
              </td>
            </>
          )}
          <td>{dateTimeFormat(x.modifiedOn)}</td>
          <td>{x.modifiedBy}</td>
          <td>{!isLegacy && t(`languageCodes.${language}`)}</td>
          {(flowType === 'ESRS' || flowType === 'SR_ESPF') && data.status !== 'APPROVED' && (
            <td>{createEsapValue()}</td>
          )}
          {!isLegacy && <td>{renderRelateLink()}</td>}
          <td>
            <div className="d-flex">
              <div>
                <Button
                  htmlId="currentDownload"
                  className="btn-tertiary no-padding"
                  icon="file-download"
                  text=""
                  onClick={() =>
                    isLegacy ? downloadEzshare(x.ezShareDocumentCode) : handleDownloadDoc(x.id)
                  }
                />

                <UncontrolledTooltip
                  className="tooltip-impact"
                  placement="bottom"
                  target="currentDownload"
                  modifiers={tooltipPreventOverflowModifiers}
                >
                  {t('download')}
                </UncontrolledTooltip>
              </div>
              {canEdit && (
                <>
                  <div id="divUpload">
                    <FileUploadButton
                      htmlId="btnUpload"
                      text=""
                      onClick={handleUpload}
                      onError={uploadFileError}
                      className="float-right"
                      icon="file-upload"
                      btnClass="no-padding"
                      wrapperId="wrapperUpload"
                    />
                    {errorUpload && <span className="form__form-group-error">{errorUpload}</span>}
                  </div>
                  <UncontrolledTooltip
                    className="tooltip-impact"
                    placement="bottom"
                    target="divUpload"
                    modifiers={tooltipPreventOverflowModifiers}
                  >
                    {t('upload')}
                  </UncontrolledTooltip>
                </>
              )}
              {showBtnReviewers && !isLegacy && (
                <>
                  <div id="divReviewers">
                    <Button
                      htmlId="btnReviewer"
                      text=""
                      onClick={() => setShowReviewersModal(true)}
                      className="btn-tertiary no-padding"
                      icon="users"
                    />
                  </div>
                  <UncontrolledTooltip
                    className="tooltip-impact"
                    placement="bottom"
                    target="divReviewers"
                    modifiers={tooltipPreventOverflowModifiers}
                  >
                    {t('Reviewers')}
                  </UncontrolledTooltip>
                </>
              )}
              {canDelete && (
                <>
                  <div id="divDelete">
                    <Button
                      htmlId="btnDelete"
                      text=""
                      onClick={() => setShowDeleteFlowModal(true)}
                      className="btn-tertiary no-padding"
                      icon="trash-alt"
                      disabled={isChildren() || isLegacy}
                    />
                  </div>
                  <UncontrolledTooltip
                    className="tooltip-impact"
                    placement="bottom"
                    target="divDelete"
                    modifiers={tooltipPreventOverflowModifiers}
                  >
                    {isChildren()
                      ? t('warningDeleteRelatedFlow', { flowType: flowTypeName })
                      : t('Delete')}
                  </UncontrolledTooltip>
                </>
              )}
            </div>
          </td>
        </tr>
        {(flowType === 'ESRS' || flowType === 'SR_ESPF') &&
          data.status === 'APPROVED' &&
          currentEsapHasContent &&
          lastEsap && <CurrentEsap data={lastEsap} flowType={flowType} />}
      </>
    );
  };

  return (
    <>
      <div className="last-version-content table-responsive">
        <Table className="flow-table">
          <thead>
            <tr>
              <th>{t('currentVersion')}</th>
              <th>{t('status')}</th>
              {isSR && (
                <>
                  <th>{t('spr')}</th>
                  <th>{t('esrr')}</th>
                  <th>{t('sprRationale')}</th>
                </>
              )}
              <th>{t('lastModification')}</th>
              <th>{t('modifiedBy')}</th>
              <th>{t('language')}</th>
              {(flowType === 'ESRS' || flowType === 'SR_ESPF') && data.status !== 'APPROVED' && (
                <th>{t('esap')}</th>
              )}
              {!isLegacy && <th>{t('relatedOperations')}</th>}
              <th style={{ width: '85px' }}>{t('actions')}</th>
            </tr>
          </thead>
          <tbody>{versionTr(data)}</tbody>
        </Table>
      </div>
      <Modal
        title={t('rationale')}
        visible={showRationaleModal}
        setVisible={() => setShowRationaleModal(false)}
        actions={[{ text: 'Close', action: () => setShowRationaleModal(false) }]}
        customClass="extra_wide-modal"
        onClosed={() => setShowRationaleModal(false)}
      >
        <SprTable data={data.spr} />
      </Modal>
      {canRelate && (
        <ConfirmModal
          visible={showRelateModal}
          setVisible={() => setShowRelateModal(false)}
          title={t('flowRelateTitle')}
          action={handleRelate}
          actionDisabled={maxRelated()}
          buttonText={t('apply')}
        >
          <FlowRelatedSelection
            currentValues={relatedOperations}
            setRelatedOperationIds={setRelatedOperationIds}
            flowType={flowType}
          />
        </ConfirmModal>
      )}
      {canDelete && (
        <FlowDeleteModal
          visible={showDeleteFlowModal}
          setVisible={setShowDeleteFlowModal}
          callback={deleteAction}
          relatedOperations={[...relatedOperations, operationOwner].filter((x) => x.id !== opId)}
        />
      )}
    </>
  );
};

export default FlowCurrentVersionsTable;
